import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { UserAvatar } from "@/components/ui/UserAvatar";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { MessageSquare, Plus } from "lucide-react";
import {
  useCohortDiscussions,
  useCreateCohortDiscussion,
  type CohortDiscussion,
} from "@/hooks/useCohortDiscussions";
import { useAuth } from "@/lib/auth";
import { useToast } from "@/hooks/use-toast";
import { formatDistanceToNow } from "date-fns";
import { CohortDiscussionThread } from "./CohortDiscussionThread";
import { UserName } from "@/components/badges/UserName";

interface Props {
  cohortId: string;
}

export function CohortDiscussionList({ cohortId }: Props) {
  const { data: discussions, isLoading } = useCohortDiscussions(cohortId);
  const [selectedDiscussionId, setSelectedDiscussionId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  if (selectedDiscussionId) {
    return (
      <CohortDiscussionThread
        discussionId={selectedDiscussionId}
        cohortId={cohortId}
        onBack={() => setSelectedDiscussionId(null)}
      />
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[...Array(3)].map((_, i) => (
          <Skeleton key={i} className="h-24 w-full" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Cohort Discussions</h3>
        <Button onClick={() => setShowCreate(true)} size="sm">
          <Plus className="h-4 w-4 mr-2" />
          Start a Discussion
        </Button>
      </div>

      {discussions && discussions.length > 0 ? (
        <div className="space-y-3">
          {discussions.map((d) => (
            <CohortDiscussionCard
              key={d.id}
              discussion={d}
              onClick={() => setSelectedDiscussionId(d.id)}
            />
          ))}
        </div>
      ) : (
        <Card className="border-dashed">
          <CardContent className="py-12 text-center">
            <MessageSquare className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-sm text-muted-foreground mb-4">
              No discussions yet. Start the first one.
            </p>
            <Button onClick={() => setShowCreate(true)} variant="outline">
              <Plus className="h-4 w-4 mr-2" />
              Start a Discussion
            </Button>
          </CardContent>
        </Card>
      )}

      <CreateCohortDiscussionDialog
        cohortId={cohortId}
        open={showCreate}
        onOpenChange={setShowCreate}
      />
    </div>
  );
}

function CohortDiscussionCard({
  discussion,
  onClick,
}: {
  discussion: CohortDiscussion;
  onClick: () => void;
}) {
  return (
    <Card
      className="cursor-pointer hover:border-primary/50 transition-colors"
      onClick={onClick}
    >
      <CardContent className="p-4">
        <div className="flex gap-3">
          <UserAvatar avatarPath={discussion.author?.avatar_url} fullName={discussion.author?.full_name} className="h-10 w-10 flex-shrink-0" fallbackClassName="bg-primary text-primary-foreground" thumbnailSize={64} />

          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <h4 className="font-medium truncate">{discussion.title}</h4>
              <Badge variant="secondary" className="flex-shrink-0">
                <MessageSquare className="h-3 w-3 mr-1" />
                {discussion.reply_count || 0}
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground line-clamp-2 mt-1">
              {discussion.content}
            </p>
            <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
              <span><UserName userId={discussion.user_id} name={discussion.author?.full_name} /></span>
              <span>•</span>
              <span>
                {formatDistanceToNow(new Date(discussion.created_at), {
                  addSuffix: true,
                })}
              </span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function CreateCohortDiscussionDialog({
  cohortId,
  open,
  onOpenChange,
}: {
  cohortId: string;
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const { user } = useAuth();
  const { toast } = useToast();
  const create = useCreateCohortDiscussion();
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");

  const handleSubmit = async () => {
    if (!user || !title.trim() || !content.trim()) return;
    try {
      await create.mutateAsync({
        cohortId,
        title: title.trim(),
        content: content.trim(),
        userId: user.id,
      });
      toast({ title: "Discussion created" });
      setTitle("");
      setContent("");
      onOpenChange(false);
    } catch {
      toast({
        title: "Error",
        description: "Failed to create discussion.",
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Start a Discussion</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          <div className="space-y-2">
            <Label>Title</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="What's this about?"
            />
          </div>
          <div className="space-y-2">
            <Label>Message</Label>
            <Textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={5}
              placeholder="Share your thoughts..."
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!title.trim() || !content.trim() || create.isPending}
          >
            {create.isPending ? "Posting..." : "Post Discussion"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
